import { ChangeEventHandler, useEffect, useMemo, useState } from "react";

import sortBy from "lodash/sortBy";
import { useFlags } from "launchdarkly-react-client-sdk";

import {
  MESSAGE_EVENT_FILTER_CONFIGS,
  MessageEventGroup,
  type MessageEventFilterConfig,
} from "src/cep/types";
import { isRealtimeEventModel } from "src/utils/event-models";
import { createRelationshipOptionLabeler } from "src/utils/relationship-names";
import { ChannelType } from "src/types/cep";

import {
  Column,
  ColumnType,
  Condition,
  ConditionType,
  getInitialTraitColumn,
  isRelatedColumn,
  isTraitColumn,
  RawColumn,
  isTransformedColumn,
  TraitConfig,
  TraitType,
  ModelSemanticType,
} from "src/types/visual";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { getTraitPropertyType } from "src/components/explore/visual/utils";

import {
  FilterOption,
  MESSAGE_EVENT_CHANNEL_ICON_OVERRIDES,
} from "./constants";
import {
  EventFilterOption,
  EventPropertyFilterOption,
  FilterColumnOption,
  InlineTraitFilterOption,
  JourneyFilterOption,
  MemberStateFilterOption,
  MessageEventFilterOption,
  NumberOfFilterOption,
  PropertyFilterOption,
  SegmentSetFilterOption,
  TraitFilterOption,
} from "./types";
import {
  buildSelectedColumnIndex,
  getSelectedColumnFromIndex,
  getPropertyLabel,
  columnReferenceKey,
} from "./utils";
import { useAIColumnSuggestions } from "./use-ai-column-suggestions";
import { LightningBoltIcon } from "@hightouch/htui";
import { getJourneyState } from "src/pages/journeys/utils/journey-state";
import { getNumberOfUsersInJourney } from "src/pages/journeys/utils/get-number-of-users-in-journey";
import { JourneyStatus } from "src/types/journeys";
import { useFilterableOptionsContext } from "./filterable-options-hook";
import { findDuplicates } from "src/utils/find-duplicates";
import { getRtaMembershipReferenceDisabledReason } from "src/components/audiences/rta-membership-reference";
import {
  isObjectArrayItemColumn,
  objectArrayParentNamesByModel,
} from "src/pages/schema/columns/object-array-columns";

export type OptionGroups<T extends FilterColumnOption = FilterColumnOption> =
  Array<{
    label: string;
    options: T[];
  }>;

export function filterGroups<T extends FilterColumnOption>(
  groups: OptionGroups<T>,
  search: string,
): OptionGroups<T> {
  const searchLower = search.trim().toLowerCase();
  if (!searchLower) return groups;
  return groups.map(({ label, options }) => ({
    label,
    options: options.filter(
      ({ label: optLabel, description }) =>
        optLabel?.trim().toLowerCase().includes(searchLower) ||
        description?.toLowerCase().includes(searchLower),
    ),
  }));
}

export function injectAISuggestions(
  filteredGroups: OptionGroups,
  suggestedColumns: Column[],
  allPropertyOptions: FilterColumnOption[],
): OptionGroups {
  const propertyOptionsByKey = new Map<string, FilterColumnOption>();
  for (const opt of allPropertyOptions) {
    if (opt.type === FilterOption.Property && typeof opt.value === "object") {
      propertyOptionsByKey.set(columnReferenceKey(opt.value), opt);
    }
  }

  const matchedKeys = new Set<string>();
  const aiSuggestions = suggestedColumns
    .map((suggestedCol) => {
      const key = columnReferenceKey(suggestedCol);
      const match = propertyOptionsByKey.get(key);
      if (match) matchedKeys.add(key);
      return match;
    })
    .filter((s): s is FilterColumnOption => s !== undefined)
    .map((s) => ({ ...s, isAISuggested: true }));

  return filteredGroups.map((g, i) => {
    if (i !== 0) return g;
    const regularColumns = g.options.filter(
      (col) =>
        col.type !== FilterOption.Property ||
        typeof col.value === "string" ||
        !matchedKeys.has(columnReferenceKey(col.value)),
    );
    return { ...g, options: [...aiSuggestions, ...regularColumns] };
  });
}

export interface UseConditionSelect<TCondition> {
  condition?: TCondition;
  parentModelId?: string | null;
}

const customTraitOption: InlineTraitFilterOption = {
  type: FilterOption.InlineTrait,
  label: "Create a custom trait",
  // This is not a real filter option, it acts more like a button in the UI
  // so these keys are intentionally set to null/undefined
  value: null,
  description: undefined,
  usageRatio: undefined,
};

export const CREATE_JOURNEY_TRAIT_LABEL = "Create journey trait";

const createJourneyTraitOption: InlineTraitFilterOption = {
  type: FilterOption.InlineTrait,
  label: CREATE_JOURNEY_TRAIT_LABEL,
  value: null,
  description: undefined,
  usageRatio: undefined,
};

export const useConditionSelect = <TCondition extends Condition>({
  condition,
  parentModelId,
}: UseConditionSelect<TCondition>) => {
  const { restrictMergedColumns } = useFlags();
  const [search, setSearch] = useState("");
  const [refreshSelectedTab, setRefreshSelectedTab] = useState(true);

  const {
    audienceId,
    audienceReferenceStats,
    selectedEvent,
    enableReferencingEntryEvent,
    realtimeConditionScope,
    onCreateJourneyTrait,
  } = useQueryBuilderContext();

  const isAudienceCreated = audienceId != null;
  const isRealtimeAudience = realtimeConditionScope === "audience";
  const isRealtimeJourneyCriteria =
    realtimeConditionScope === "journey-criteria";
  const { realtimeObjectArrayFilters = false } = useFlags();

  const {
    nestingLevel,
    columns,
    events,
    messageEvents,
    relationships,
    traits,
    relatedAudiences,
    journeys,
    isLoading: filterableContextIsLoading,
    sections,
    filterableColumnsByModelId,
    managedColumnNames,
    setAudienceSearch,
    parentSnapshottingEnabled,
    rtaWritebackReadsIsEnabled,
  } = useFilterableOptionsContext();

  const [filter, setFilter] = useState<FilterOption>(
    sections[0]?.type ?? FilterOption.All,
  );

  // Tabs that surface audience-typed options (and so should drive the
  // server-side audience search). All other tabs filter client-side over
  // their own option list and should not refire the audience query.
  const tabIncludesAudiences = (f: FilterOption) =>
    f === FilterOption.All || f === FilterOption.Audience;

  const updateSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value;
    setSearch(value);
    if (tabIncludesAudiences(filter)) {
      setAudienceSearch(value);
    }
  };

  const changeFilter = (filterType: FilterOption) => {
    setFilter(filterType);
    if (!tabIncludesAudiences(filterType)) {
      setAudienceSearch("");
    }
  };

  const reset = () => {
    setFilter(sections[0]?.type ?? FilterOption.All);
    setSearch("");
    setAudienceSearch("");
  };

  const propertyOptions: OptionGroups<PropertyFilterOption> = useMemo(() => {
    if (!sections.find((s) => s.type === FilterOption.Property)) {
      return [{ label: "Properties", options: [] }];
    }
    const filteredColumns =
      restrictMergedColumns && nestingLevel === 0
        ? columns.filter((column) => column.column_reference.type === "raw")
        : columns;

    const duplicateColumnNames = findDuplicates(columns, (c) => c.name);

    // Build a (modelId, name) -> ratio lookup once instead of doing a linear
    // `.find()` over `audienceReferenceStats.modelColumns` for every column
    // in `filteredColumns`. Both arrays scale with workspace size, so the
    // original O(n × m) lookup hit millions of comparisons on workspaces
    // with thousands of columns.
    //
    // A nested Map (rather than a single key like `${modelId}:${name}`)
    // avoids any theoretical separator collision in column names, and the
    // `has()` guard preserves the original `.find()`'s first-match-wins
    // semantics in the unlikely event of a duplicate (modelId, name) pair.
    const usageRatioByModel = new Map<string, Map<string, number>>();
    for (const stat of audienceReferenceStats?.modelColumns ?? []) {
      let byName = usageRatioByModel.get(stat.modelId);
      if (!byName) {
        byName = new Map();
        usageRatioByModel.set(stat.modelId, byName);
      }
      if (!byName.has(stat.name)) {
        byName.set(stat.name, stat.ratio);
      }
    }

    const objectArrayParentsByModel = objectArrayParentNamesByModel(
      filteredColumns.map((column) => ({
        modelId: column.model_id.toString(),
        name: column.name,
        type: column.custom_type ?? column.type,
      })),
    );
    const pickerColumns = filteredColumns.filter((column) => {
      if (
        isObjectArrayItemColumn(
          column.name,
          objectArrayParentsByModel.get(column.model_id.toString()),
        )
      ) {
        return false;
      }
      return (
        realtimeObjectArrayFilters ||
        (column.custom_type ?? column.type) !== ColumnType.JsonArrayObjects
      );
    });

    const toOption = ({
      alias,
      name,
      column_reference,
      custom_type,
      description,
      type,
      model_id,
      model_name,
      case_sensitive,
      top_k_enabled,
      merged_relationship_names,
    }: (typeof filteredColumns)[number]): PropertyFilterOption => ({
      case_sensitive,
      description,
      label: getPropertyLabel(
        alias,
        name,
        duplicateColumnNames.has(name) ? merged_relationship_names : [],
      ),
      modelName: model_name,
      propertyType: (custom_type ?? type) as ColumnType,
      type: FilterOption.Property,
      value: column_reference as RawColumn,
      suggestionsEnabled: Boolean(top_k_enabled),
      usageRatio: usageRatioByModel.get(model_id.toString())?.get(name),
      isHightouchManaged: managedColumnNames.has(name),
    });

    const allOptions = sortBy(pickerColumns.map(toOption), (col) =>
      col.label.toLowerCase(),
    );

    const managedOptions = allOptions.filter((col) => col.isHightouchManaged);

    const groups: OptionGroups<PropertyFilterOption> = [
      { label: "All", options: allOptions },
    ];

    if (managedOptions.length > 0) {
      groups.push({ label: "Managed by Hightouch", options: managedOptions });
    }

    return groups;
  }, [
    columns,
    audienceReferenceStats?.modelColumns,
    managedColumnNames,
    nestingLevel,
    restrictMergedColumns,
    sections,
    realtimeObjectArrayFilters,
  ]);

  // TODO(nolan): should to rework this so that
  // we only fetch trait related model filterable columns
  // inside the trait subconditions context. I don't think they're
  // used in the preview so I don't think they're needed here?
  // They were fetched here when we fetched everything at root level.
  const traitOptions: OptionGroups<TraitFilterOption> = useMemo(() => {
    if (
      !sections.find((s) =>
        [FilterOption.Trait, FilterOption.TraitTemplate].includes(s.type),
      )
    ) {
      return [{ label: "Traits", options: [] }];
    }
    const options: TraitFilterOption[] = traits
      .filter((trait) => trait.type)
      .map((trait) => {
        const traitFilterableColumns =
          filterableColumnsByModelId[trait.relationship?.to_model?.id] ?? [];

        return {
          label: trait.name,
          value:
            condition?.type === ConditionType.Property &&
            (isRelatedColumn(condition.property) ||
              isTransformedColumn(condition.property)) &&
            isTraitColumn(condition.property.column) &&
            condition?.property?.column.traitDefinitionId === trait.id
              ? condition.property
              : getInitialTraitColumn(trait),
          description: trait.description,
          propertyType:
            getTraitPropertyType(trait, traitFilterableColumns) ??
            ColumnType.Unknown,
          type: trait.is_template
            ? FilterOption.TraitTemplate
            : FilterOption.Trait,
          traitType: trait.type as TraitType,
          traitConfig: trait.config as TraitConfig,
          parentModel: trait.parentModel,
          relatedModel: trait.relationship?.to_model
            ? {
                ...trait.relationship.to_model,
                filterable_audience_columns: traitFilterableColumns,
              }
            : undefined,
          traitId: trait.id,
          traitPredictor: trait.predictor,
          suggestionsEnabled: Boolean(trait.top_k_enabled),
          usageRatio: audienceReferenceStats?.traits.find(
            (t) => t.traitDefinitionId === trait.id,
          )?.ratio,
        };
      });

    return [
      {
        label: "Traits",
        options: sortBy(options, (o) => o.label.toLowerCase()),
      },
    ];
  }, [
    traits,
    audienceReferenceStats?.traits,
    filterableColumnsByModelId,
    condition,
    sections,
  ]);

  const eventOptions: OptionGroups<
    EventFilterOption | EventPropertyFilterOption
  > = useMemo(() => {
    if (
      !sections.find((s) =>
        [
          FilterOption.Event,
          FilterOption.EventProperty,
          FilterOption.RealtimeEvent,
        ].includes(s.type),
      )
    ) {
      return [{ label: "Events", options: [] }];
    }
    const getEventLabel = createRelationshipOptionLabeler(events);
    return [
      {
        label: "Events",
        options: sortBy(
          events?.flatMap((relationship) => {
            const {
              id: relationshipId,
              to_model: { id: eventModelId, description, semantic_type, event },
            } = relationship;
            const label = getEventLabel(relationship);
            const eventOption: EventFilterOption = {
              label,
              description,
              semanticType: semantic_type as ModelSemanticType | null,
              value: { eventModelId, relationshipId },
              type: isRealtimeEventModel(event)
                ? FilterOption.RealtimeEvent
                : FilterOption.Event,
              // Realtime events are not available to existing non-realtime audiences
              // These should already be filtered out upstream. But we're doing this as a defensive check.
              isDisabled: Boolean(
                isRealtimeEventModel(event) &&
                  isAudienceCreated &&
                  !isRealtimeAudience,
              ),
              examplePropertyNames: (
                filterableColumnsByModelId[eventModelId] ?? []
              )
                .slice(0, 5)
                .map((c) => c.alias ?? c.name),
              // Note: We can use the relationship or event model to calculate the usage ratio
              // since we track both for Event Filters.
              // It shouldn't make a difference.
              usageRatio: audienceReferenceStats?.modelRelationships.find(
                (r) => r.modelRelationshipId === relationshipId.toString(),
              )?.ratio,
            };

            // If selectedEvent is provided and enableReferencingEntryEvent is true, then add the event as a selectable property (EventProperty)
            if (
              enableReferencingEntryEvent &&
              eventModelId === selectedEvent?.eventModelId &&
              relationshipId === selectedEvent?.relationshipId
            ) {
              return [
                {
                  label,
                  description: "The specific event that started this journey",
                  value: { modelId: eventModelId },
                  invertIconColors: true,
                  type: FilterOption.EventProperty,
                  isDisabled: false,
                  examplePropertyNames: (
                    filterableColumnsByModelId[eventModelId] ?? []
                  )
                    .slice(0, 5)
                    .map((c) => c.alias ?? c.name),
                  // Note: We can use the relationship or event model to calculate the usage ratio
                  // since we track both for Event Filters.
                  // It shouldn't make a difference.
                  usageRatio: audienceReferenceStats?.modelRelationships.find(
                    (r) => r.modelRelationshipId === relationshipId.toString(),
                  )?.ratio,
                } satisfies EventPropertyFilterOption,
                eventOption,
              ];
            }

            return [eventOption];
          }),
          (column) => Boolean(column.isDisabled),
          (column) => column.type !== FilterOption.EventProperty,
          (column) => column.label.toLowerCase(),
        ),
      },
    ];
  }, [
    events,
    audienceReferenceStats?.modelRelationships,
    filterableColumnsByModelId,
    selectedEvent?.eventModelId,
    selectedEvent?.relationshipId,
    isRealtimeAudience,
    enableReferencingEntryEvent,
    sections,
    isAudienceCreated,
  ]);

  const messageEventOptions: OptionGroups<MessageEventFilterOption> =
    useMemo(() => {
      const modelsByType = new Map(
        messageEvents
          .filter((e) => e.to_model.cep_managed_model?.type)
          .map((e) => [
            e.to_model.cep_managed_model!.type,
            { eventModelId: e.to_model.id, relationshipId: e.id },
          ]),
      );

      const buildOption = (
        channel: ChannelType,
        group: MessageEventGroup,
        config: MessageEventFilterConfig,
      ): MessageEventFilterOption | null => {
        const primary = modelsByType.get(config.eventType);
        const funnel = config.funnelType && modelsByType.get(config.funnelType);
        if (!primary || (config.funnelType && !funnel)) return null;

        return {
          label: config.label,
          value: {
            eventModelId: primary.eventModelId,
            relationshipId: primary.relationshipId,
            funnelCondition: funnel
              ? {
                  type: ConditionType.Funnel,
                  eventModelId: funnel.eventModelId,
                  relationshipId: funnel.relationshipId,
                  didPerform: config.didPerformFunnel ?? false,
                }
              : undefined,
          },
          invertIconColors: true,
          type: FilterOption.MessageEvent as const,
          group,
          iconOverride: MESSAGE_EVENT_CHANNEL_ICON_OVERRIDES[channel],
          isDisabled: false,
          examplePropertyNames: [],
          semanticType: null,
          usageRatio: undefined,
        };
      };

      const buildChannelOptions = (
        channel: ChannelType,
      ): MessageEventFilterOption[] =>
        Object.entries(MESSAGE_EVENT_FILTER_CONFIGS[channel]).flatMap(
          ([group, configs]) =>
            (configs ?? []).flatMap((config) => {
              const option = buildOption(
                channel,
                group as MessageEventGroup,
                config,
              );
              return option ? [option] : [];
            }),
        );

      return [
        { label: "Email", options: buildChannelOptions(ChannelType.Email) },
        { label: "SMS", options: buildChannelOptions(ChannelType.Sms) },
        { label: "Push", options: buildChannelOptions(ChannelType.Push) },
      ].filter(({ options }) => options.length > 0);
    }, [messageEvents]);

  // Don't show related events under the options. Users should use the
  // EventConditionFilter for creating queries based on events.
  const relatedModelOptions: OptionGroups<NumberOfFilterOption> =
    useMemo(() => {
      if (!sections.find((s) => s.type === FilterOption.Relation)) {
        return [{ label: "Related models", options: [] }];
      }
      const getRelatedModelLabel =
        createRelationshipOptionLabeler(relationships);

      return [
        {
          label: "Related models",
          options: sortBy(
            relationships
              ?.filter(({ to_model: { event } }) => !event)
              ?.map((relationship) => {
                const {
                  id,
                  to_model: {
                    id: toModelId,
                    description,
                    semantic_type,
                    cep_managed_model,
                  },
                } = relationship;
                return {
                  value: { relationshipId: id },
                  label: getRelatedModelLabel(relationship),
                  description,
                  semanticType: semantic_type as ModelSemanticType | null,
                  toModelId,
                  type: FilterOption.Relation,
                  examplePropertyNames: (
                    filterableColumnsByModelId[toModelId] ?? []
                  )
                    .slice(0, 5)
                    .map((c) => c.alias ?? c.name),
                  usageRatio: audienceReferenceStats?.modelRelationships.find(
                    (r) => r.modelRelationshipId === id.toString(),
                  )?.ratio,
                  isHightouchManaged: cep_managed_model?.type !== undefined,
                };
              }),
            (column) => column.label.toLowerCase(),
          ),
        },
      ];
    }, [
      relationships,
      audienceReferenceStats?.modelRelationships,
      filterableColumnsByModelId,
      sections,
    ]);

  const audienceOptions: OptionGroups<SegmentSetFilterOption> = useMemo(() => {
    if (!sections.find((s) => s.type === FilterOption.Audience)) {
      return [{ label: "Audiences", options: [] }];
    }
    return [
      {
        label: "Audiences",
        options: sortBy(
          relatedAudiences?.map(
            ({ id, name, is_realtime_audience, static_list, ...data }) => {
              // Audience snapshots exist so a batch query can read realtime
              // membership out of the warehouse. A realtime journey reads
              // membership from the live store instead, so snapshotting is not
              // a prerequisite for referencing an RTA here.
              const disabledReason = isRealtimeJourneyCriteria
                ? undefined
                : getRtaMembershipReferenceDisabledReason({
                    isRealtimeAudience: is_realtime_audience,
                    rtaWritebackReadsIsEnabled,
                    parentSnapshottingEnabled,
                  });

              return {
                ...data,
                label: name,
                value: { modelId: id },
                type: FilterOption.Audience,
                ...(is_realtime_audience
                  ? {
                      badge: {
                        variant: "info" as const,
                        icon: LightningBoltIcon,
                        label: "Realtime",
                      },
                    }
                  : static_list
                    ? {
                        badge: {
                          variant: "ghost" as const,
                          label: "Static",
                        },
                      }
                    : {}),
                disabledReason,
                isDisabled: disabledReason != null,
                usageRatio: audienceReferenceStats?.segments.find(
                  (r) => r.segmentId === id.toString(),
                )?.ratio,
              } satisfies SegmentSetFilterOption;
            },
          ),
          (column) => column.label.toLowerCase(),
        ),
      },
    ];
  }, [
    relatedAudiences,
    audienceReferenceStats?.segments,
    sections,
    parentSnapshottingEnabled,
    rtaWritebackReadsIsEnabled,
    isRealtimeJourneyCriteria,
  ]);

  const journeyOptions: OptionGroups<JourneyFilterOption> = useMemo(() => {
    if (!sections.find((s) => s.type === FilterOption.Journey)) {
      return [{ label: "Journeys", options: [] }];
    }
    return [
      {
        label: "Journeys",
        options: sortBy(
          journeys.map((journey) => ({
            label: journey.name,
            value: { journeyId: journey.id },
            state: getJourneyState({
              status: journey.status as JourneyStatus,
              reset: Boolean(journey.reset),
              numberOfUsersInJourney: getNumberOfUsersInJourney(journey.nodes),
              numberOfPreviousRuns:
                journey.runs_aggregate.aggregate?.count ?? 0,
            }),
            description: journey.description,
            type: FilterOption.Journey,
            syncs: journey.nodes.flatMap((node) =>
              node.sync_configs
                .flatMap((sync_config) => sync_config.sync)
                .filter(
                  (sync): sync is NonNullable<typeof sync> =>
                    sync !== null && sync.destination !== null,
                ),
            ),
            stats: {
              numberOfUsersInJourney: getNumberOfUsersInJourney(journey.nodes),
              journeyEntryCount: journey.most_recent_run[0]?.entry_count ?? 0,
              journeyExitCount: journey.most_recent_run[0]?.exit_count ?? 0,
              journeyCompletedCount:
                journey.most_recent_run[0]?.completed_count ?? 0,
              lastRunFinishedAt: journey.most_recent_run[0]?.finished_at,
            },
            examplePropertyNames: [],
            usageRatio: audienceReferenceStats?.journeys.find(
              (j) => j.journeyId === journey.id,
            )?.ratio,
          })),
          (column) => column.label.toLowerCase(),
        ),
      },
    ];
  }, [journeys, audienceReferenceStats?.journeys, sections]);

  const journeyMemberStateOptions: OptionGroups<MemberStateFilterOption> =
    useMemo(() => {
      if (!sections.find((s) => s.type === FilterOption.JourneyMemberState)) {
        return [];
      }
      return [
        {
          label: "Member state",
          options: [
            {
              type: FilterOption.JourneyMemberState,
              label: "Journey entry time",
              description: "Time when the user entered this journey",
              value: { type: "journey_member_state", field: "entered_at" },
              propertyType: ColumnType.Timestamp,
              usageRatio: undefined,
            },
          ],
        },
      ];
    }, [sections]);

  const allColumns = useMemo(() => {
    // Property options has an "All" group plus filtered sub-groups (e.g.
    // "Managed by Hightouch") that re-use the same option references, so
    // flattening produces duplicates that need to be removed. The previous
    // `uniqWith(arr, isEqual)` was O(n²) with deep equality on every pair,
    // which dominated render time on workspaces with thousands of columns.
    //
    // A reference-identity Set is O(n) and matches what `uniqWith(isEqual)`
    // was actually catching here (sub-groups built via `.filter()` share
    // refs with the parent "All" group). Keying on a derived value
    // (e.g. the column reference) would be more aggressive, collapsing
    // structurally-equal options with differing metadata — a semantic
    // change we don't want bundled with this perf fix.
    const seenOptions = new Set<PropertyFilterOption>();
    const uniqueProperties: PropertyFilterOption[] = [];
    for (const option of propertyOptions.flatMap((g) => g.options)) {
      if (!seenOptions.has(option)) {
        seenOptions.add(option);
        uniqueProperties.push(option);
      }
    }

    return sortBy(
      [
        ...uniqueProperties,
        ...traitOptions.flatMap((g) => g.options),
        ...journeyMemberStateOptions.flatMap((g) => g.options),
        ...eventOptions.flatMap((g) => g.options),
        ...messageEventOptions.flatMap((g) => g.options),
        ...relatedModelOptions.flatMap((g) => g.options),
        ...audienceOptions.flatMap((g) => g.options),
        ...journeyOptions.flatMap((g) => g.options),
      ],
      (column) => Boolean(column.isDisabled),
      (column) => column.type !== FilterOption.EventProperty,
      (columns) => columns.label.toLowerCase(),
    );
  }, [
    propertyOptions,
    traitOptions,
    journeyMemberStateOptions,
    eventOptions,
    messageEventOptions,
    relatedModelOptions,
    audienceOptions,
    journeyOptions,
  ]);

  // Hide "Create a custom trait" on realtime surfaces: both restrict which
  // traits are offered (a realtime journey to realtime traits, a realtime
  // audience to traits its evaluators support), so a trait created here is not
  // guaranteed to appear in the picker it was created from.
  const hideCustomTraitOption = isRealtimeAudience || isRealtimeJourneyCriteria;
  const showCreateJourneyTrait =
    isRealtimeJourneyCriteria && Boolean(onCreateJourneyTrait);

  const injectedTraitOptions = showCreateJourneyTrait
    ? [
        {
          label: "Traits",
          options: [
            createJourneyTraitOption,
            ...traitOptions.flatMap((g) => g.options),
          ],
        },
      ]
    : hideCustomTraitOption
      ? traitOptions
      : [
          {
            label: "Traits",
            options: [
              customTraitOption,
              ...traitOptions.flatMap((g) => g.options),
            ],
          },
        ];

  const optionsByFilter = useMemo(
    () => ({
      [FilterOption.All]: [{ label: "All", options: allColumns }],
      [FilterOption.Property]: propertyOptions,
      [FilterOption.Event]: eventOptions,
      [FilterOption.MessageEvent]: messageEventOptions,
      [FilterOption.Audience]: audienceOptions,
      [FilterOption.Relation]: relatedModelOptions,
      [FilterOption.Trait]:
        showCreateJourneyTrait || nestingLevel === 0
          ? injectedTraitOptions
          : traitOptions,
      [FilterOption.TraitTemplate]: hideCustomTraitOption
        ? traitOptions
        : [
            {
              label: "Traits",
              options: [
                customTraitOption,
                ...traitOptions.flatMap((g) => g.options),
              ],
            },
          ],
      [FilterOption.Journey]: journeyOptions,
      [FilterOption.JourneyMemberState]: journeyMemberStateOptions,
    }),
    [
      allColumns,
      propertyOptions,
      eventOptions,
      messageEventOptions,
      relatedModelOptions,
      audienceOptions,
      traitOptions,
      journeyOptions,
      journeyMemberStateOptions,
      nestingLevel,
      hideCustomTraitOption,
      showCreateJourneyTrait,
      injectedTraitOptions,
    ],
  );

  const {
    suggestedColumns,
    isLoading: aiSuggestionsLoading,
    isEnabled: aiSuggestionsEnabled,
  } = useAIColumnSuggestions({
    parentModelId: parentModelId ?? undefined,
    search,
    // TODO: Enable AI suggestions for all filters
    enabled: filter === FilterOption.Property || filter === FilterOption.All,
  });

  // Build the lookup index once per `allColumns` change. The underlying
  // `getSelectedColumn` walk did up to four sequential O(allColumns) scans
  // per call; on audiences with many conditions and workspaces with
  // thousands of columns, that cost multiplied. The index turns each
  // lookup into O(1).
  const selectedColumnIndex = useMemo(
    () => buildSelectedColumnIndex(allColumns),
    [allColumns],
  );

  const selectedColumn = useMemo(() => {
    if (!condition) {
      return undefined;
    }

    const found = getSelectedColumnFromIndex(condition, selectedColumnIndex);

    if (
      found?.type === FilterOption.MessageEvent &&
      condition.type === ConditionType.Event
    ) {
      const messageEvent = messageEvents.find(
        (e) =>
          e.to_model.id.toString() === condition.eventModelId?.toString() &&
          e.id.toString() === condition.relationshipId?.toString(),
      );
      if (messageEvent) {
        return { ...found, label: messageEvent.to_model.name };
      }
    }

    return found;
  }, [condition, selectedColumnIndex, messageEvents]);

  // Popover stays mounted, so may need to be triggered externally.
  useEffect(() => {
    if (refreshSelectedTab) {
      const newFilter = getSelectedColumnFromIndex(
        condition,
        selectedColumnIndex,
      )?.type;

      if (newFilter) {
        setFilter(newFilter);
      }

      setRefreshSelectedTab(false);
    }
  }, [selectedColumnIndex, condition, refreshSelectedTab]);

  return {
    columns: useMemo((): OptionGroups => {
      const visibleFilter =
        filter === FilterOption.EventProperty ||
        filter === FilterOption.RealtimeEvent
          ? FilterOption.Event
          : filter;
      const groups = optionsByFilter[visibleFilter];
      const filteredGroups = filterGroups(groups, search);

      if (
        aiSuggestionsEnabled &&
        suggestedColumns.length > 0 &&
        search.trim().length > 0
      ) {
        const allPropertyOptions =
          optionsByFilter[FilterOption.All]?.flatMap((g) => g.options) ?? [];
        return injectAISuggestions(
          filteredGroups,
          suggestedColumns,
          allPropertyOptions,
        );
      }

      return filteredGroups;
    }, [
      optionsByFilter,
      filter,
      search,
      suggestedColumns,
      aiSuggestionsEnabled,
    ]),
    filter,
    search,
    selectedColumn,
    filterableColumnsLoading: filterableContextIsLoading,
    aiSuggestionsLoading,
    aiSuggestionsEnabled,
    onResetSelectedTab: () => setRefreshSelectedTab(true),
    onFilterChange: changeFilter,
    onReset: reset,
    onSearch: updateSearch,
  };
};
